import { Alert, Button, FormControl, Grid, TextField } from '@mui/material';
import { ChangeEvent, FormEvent, useContext, useState } from 'react';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { ReceiptsReportRequest } from '../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { formatDateForReport } from '../../../../utils';
import { UserContext } from '../../../../components/shared/useUser';
import { MultiStorePicker, StoreSelection } from '../../../../components/shared/MultiStorePicker';

type IProps = {
  onClose?: () => void;
}

const AmRestReceiptsReportForm = (props: IProps) => {
  const { user } = useContext(UserContext);
  const [storeSelection, setStoreSelection] = useState<StoreSelection>({ mode: 'all' });
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState<boolean>(false);
  const { runAmRestReceiptsReport } = useNswagClient();
  const { t } = useTranslation('common');

  const getMaxDate = () => {
    const today = new Date().toISOString().split('T')[0];
    return today;
  };

  const getMinDate = () => {
    const minDate = new Date('1900-01-01').toISOString().split('T')[0];
    return minDate;
  };

  const handleFromDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFromDate(event.target.value);
  };

  const handleToDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setToDate(event.target.value);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setIsGenerating(true);
    setErrorMessage('');
    setSuccessMessage('');

    const from = new Date(new Date(fromDate).setHours(0, 0, 1, 0));
    const to = new Date(new Date(toDate).setHours(23, 59, 59, 0));
    const selectedAllStores = storeSelection.mode === 'all';

    const request: ReceiptsReportRequest = {
      reportOnAllAvailableStores: selectedAllStores,
      stores: selectedAllStores ? undefined : storeSelection.mode === 'specific' ? storeSelection.storeNumbers : undefined,
      from: formatDateForReport(from),
      to: formatDateForReport(to),
    };

    runAmRestReceiptsReport(request).then(() => {
      setSuccessMessage(t('reporting.generic.post-generation-message'));
      setIsRequestSubmitted(true);
    }).catch((error) => {
      if (typeof(error) === 'string') {
        setErrorMessage(error);
      }
      console.log(error);
    }).finally(() => {
      setIsGenerating(false);
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        <Grid item
          xs={12}>
          <FormControl fullWidth>
            <MultiStorePicker
              stores={user?.stores || []}
              value={storeSelection}
              onChange={setStoreSelection}
              allStoresLabel={t('reporting.all-stores') || 'All Stores'}
              label={t('reporting.select-stores')}
              placeholder={t('reporting.select-stores')}
            />
          </FormControl>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <TextField
            label={t('reporting.start-date')}
            type="date"
            required
            value={fromDate}
            onChange={handleFromDateChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{ inputProps: { min: getMinDate(), max: toDate != '' ? toDate : getMaxDate() } }}
            fullWidth
          />
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <TextField
            label={t('reporting.end-date')}
            type="date"
            required
            value={toDate}
            onChange={handleToDateChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{ inputProps: { min: fromDate != '' ? fromDate : getMinDate(), max: getMaxDate() } }}
            fullWidth
          />
        </Grid>
        {
          errorMessage && 
          <Grid item
            xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        }
        {
          successMessage && 
          <Grid item
            xs={12}>
            <Alert severity="success">{successMessage}</Alert>
          </Grid>
        }
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            variant='secondary' 
            onClick={props.onClose}>{t('cancel')}</Button>
          <Button size='lg'
            disabled={isGenerating || isRequestSubmitted}
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default AmRestReceiptsReportForm;